import {
  AnthropicApiKey,
  fetchAllAnthropicApiKeys,
  AnthropicWorkspace,
  fetchAllAnthropicWorkspaces,
  AnthropicUser,
  fetchAllAnthropicUsers
} from './anthropic-api';
import { readAdminCredentialRevision } from './storage/admin-credential-revision';
import { withProviderResourceLock } from './provider-resource-cache';

export const METADATA_CACHE_STORAGE_KEY = 'anthropic_api_key_metadata';
export const METADATA_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const METADATA_CACHE_VERSION = 2; // Bump when AnthropicApiKey schema changes

export interface AnthropicMetadataCache {
  version?: number;
  data: Record<string, AnthropicApiKey>;
  fetchedAt: number;
  refreshError?: string;
  credentialRevision?: string;
}

export const WORKSPACE_CACHE_STORAGE_KEY = 'anthropic_workspace_metadata';
export const WORKSPACE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export interface AnthropicWorkspaceCache {
  data: Record<string, AnthropicWorkspace>;
  fetchedAt: number;
  refreshError?: string;
  credentialRevision?: string;
}

export const loadAnthropicMetadataCache = (
  credentialRevision: string = readAdminCredentialRevision()
): AnthropicMetadataCache | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(METADATA_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { data, fetchedAt, version, credentialRevision: storedRevision } = parsed;
    if (!data || typeof data !== 'object' || typeof fetchedAt !== 'number') return null;
    if ((version ?? 0) < METADATA_CACHE_VERSION) {
      window.localStorage.removeItem(METADATA_CACHE_STORAGE_KEY);
      return null;
    }
    if (storedRevision !== credentialRevision) {
      window.localStorage.removeItem(METADATA_CACHE_STORAGE_KEY);
      return null;
    }
    return { version, data, fetchedAt, credentialRevision: storedRevision };
  } catch (error) {
    console.warn('Failed to parse Anthropic API key metadata cache:', error);
    return null;
  }
};

export const persistAnthropicMetadataCache = (payload: AnthropicMetadataCache) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(METADATA_CACHE_STORAGE_KEY, JSON.stringify({
      ...payload,
      version: METADATA_CACHE_VERSION,
      credentialRevision: payload.credentialRevision ?? readAdminCredentialRevision()
    }));
  } catch (error) {
    console.warn('Failed to persist Anthropic API key metadata cache:', error);
  }
};

export const getAnthropicMetadata = async (
  options: { forceRefresh?: boolean; credentialRevision?: string } = {}
): Promise<AnthropicMetadataCache> => {
  const credentialRevision = options.credentialRevision ?? readAdminCredentialRevision();
  return withProviderResourceLock(`anthropic:keys:${credentialRevision}`,
    () => refreshAnthropicMetadata({ ...options, credentialRevision }), options.forceRefresh);
};

const refreshAnthropicMetadata = async (
  options: { forceRefresh?: boolean; credentialRevision?: string } = {}
): Promise<AnthropicMetadataCache> => {
  const { forceRefresh = false } = options;
  const credentialRevision = options.credentialRevision ?? readAdminCredentialRevision();
  const cached = loadAnthropicMetadataCache(credentialRevision);
  const now = Date.now();

  if (!forceRefresh && cached && now - cached.fetchedAt < METADATA_CACHE_TTL_MS) {
    return cached;
  }

  try {
    const keys = await fetchAllAnthropicApiKeys('all');
    const data = keys.reduce<Record<string, AnthropicApiKey>>((acc, key) => {
      acc[key.id] = key;
      return acc;
    }, {});
    const payload: AnthropicMetadataCache = { data, fetchedAt: Date.now(), credentialRevision };
    persistAnthropicMetadataCache(payload);
    return payload;
  } catch (error) {
    console.error('Failed to refresh Anthropic API key metadata:', error);
    const refreshError = error instanceof Error ? error.message : String(error);
    if (cached) {
      return { ...cached, refreshError };
    }
    return { data: {}, fetchedAt: now, refreshError, credentialRevision };
  }
};

export const loadAnthropicWorkspaceCache = (
  credentialRevision: string = readAdminCredentialRevision()
): AnthropicWorkspaceCache | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(WORKSPACE_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { data, fetchedAt, credentialRevision: storedRevision } = parsed;
    if (!data || typeof data !== 'object' || typeof fetchedAt !== 'number') return null;
    if (storedRevision !== credentialRevision) {
      window.localStorage.removeItem(WORKSPACE_CACHE_STORAGE_KEY);
      return null;
    }
    return { data, fetchedAt, credentialRevision: storedRevision };
  } catch (error) {
    console.warn('Failed to parse Anthropic workspace metadata cache:', error);
    return null;
  }
};

export const persistAnthropicWorkspaceCache = (payload: AnthropicWorkspaceCache) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(WORKSPACE_CACHE_STORAGE_KEY, JSON.stringify({
      ...payload,
      credentialRevision: payload.credentialRevision ?? readAdminCredentialRevision()
    }));
  } catch (error) {
    console.warn('Failed to persist Anthropic workspace metadata cache:', error);
  }
};

export const getAnthropicWorkspaceMetadata = async (
  options: { forceRefresh?: boolean; credentialRevision?: string } = {}
): Promise<AnthropicWorkspaceCache> => {
  const credentialRevision = options.credentialRevision ?? readAdminCredentialRevision();
  return withProviderResourceLock(`anthropic:workspaces:${credentialRevision}`,
    () => refreshAnthropicWorkspaceMetadata({ ...options, credentialRevision }), options.forceRefresh);
};

const refreshAnthropicWorkspaceMetadata = async (
  options: { forceRefresh?: boolean; credentialRevision?: string } = {}
): Promise<AnthropicWorkspaceCache> => {
  const { forceRefresh = false } = options;
  const credentialRevision = options.credentialRevision ?? readAdminCredentialRevision();
  const cached = loadAnthropicWorkspaceCache(credentialRevision);
  const now = Date.now();

  if (!forceRefresh && cached && now - cached.fetchedAt < WORKSPACE_CACHE_TTL_MS) {
    return cached;
  }

  try {
    const workspaces = await fetchAllAnthropicWorkspaces(false);
    const data = workspaces.reduce<Record<string, AnthropicWorkspace>>((acc, workspace) => {
      acc[workspace.id] = workspace;
      return acc;
    }, {});
    const payload: AnthropicWorkspaceCache = { data, fetchedAt: Date.now(), credentialRevision };
    persistAnthropicWorkspaceCache(payload);
    return payload;
  } catch (error) {
    console.error('Failed to refresh Anthropic workspace metadata:', error);
    const refreshError = error instanceof Error ? error.message : String(error);
    if (cached) {
      return { ...cached, refreshError };
    }
    return { data: {}, fetchedAt: now, refreshError, credentialRevision };
  }
};

// ── User cache (user_id → AnthropicUser) ──

export const USER_CACHE_STORAGE_KEY = 'anthropic_user_metadata';
export const USER_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export interface AnthropicUserCache {
  data: Record<string, AnthropicUser>;
  fetchedAt: number;
  refreshError?: string;
  credentialRevision?: string;
}

const loadAnthropicUserCache = (
  credentialRevision: string = readAdminCredentialRevision()
): AnthropicUserCache | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(USER_CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { data, fetchedAt, credentialRevision: storedRevision } = parsed;
    if (!data || typeof data !== 'object' || typeof fetchedAt !== 'number') return null;
    if (storedRevision !== credentialRevision) {
      window.localStorage.removeItem(USER_CACHE_STORAGE_KEY);
      return null;
    }
    return { data, fetchedAt, credentialRevision: storedRevision };
  } catch {
    return null;
  }
};

const persistAnthropicUserCache = (payload: AnthropicUserCache) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(USER_CACHE_STORAGE_KEY, JSON.stringify({
      ...payload,
      credentialRevision: payload.credentialRevision ?? readAdminCredentialRevision()
    }));
  } catch {
    // ignore
  }
};

export const getAnthropicUserMetadata = async (
  options: { forceRefresh?: boolean; credentialRevision?: string } = {}
): Promise<AnthropicUserCache> => {
  const credentialRevision = options.credentialRevision ?? readAdminCredentialRevision();
  return withProviderResourceLock(`anthropic:users:${credentialRevision}`,
    () => refreshAnthropicUserMetadata({ ...options, credentialRevision }), options.forceRefresh);
};

const refreshAnthropicUserMetadata = async (
  options: { forceRefresh?: boolean; credentialRevision?: string } = {}
): Promise<AnthropicUserCache> => {
  const { forceRefresh = false } = options;
  const credentialRevision = options.credentialRevision ?? readAdminCredentialRevision();
  const cached = loadAnthropicUserCache(credentialRevision);
  const now = Date.now();

  if (!forceRefresh && cached && now - cached.fetchedAt < USER_CACHE_TTL_MS) {
    return cached;
  }

  try {
    const users = await fetchAllAnthropicUsers();
    const data = users.reduce<Record<string, AnthropicUser>>((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});
    const payload: AnthropicUserCache = { data, fetchedAt: Date.now(), credentialRevision };
    persistAnthropicUserCache(payload);
    return payload;
  } catch (error) {
    console.error('Failed to refresh Anthropic user metadata:', error);
    const refreshError = error instanceof Error ? error.message : String(error);
    if (cached) {
      return { ...cached, refreshError };
    }
    return { data: {}, fetchedAt: now, refreshError, credentialRevision };
  }
};
